import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@redux/hooks";
import {
  resetOnboardTeacher,
  onboardTeacher,
} from "@user-management-context/write/application/use-cases/teacher-onboarding/onboard-teacher";
import { selectTeacherOnboarding } from "@user-management-context/write/application/use-cases/teacher-onboarding/selectors/teacher-onboarding-selectors";
import { resetUploadDocument } from "@shared-kernel/application/use-cases/document-upload/upload-document";
import { selectDocumentUpload } from "@shared-kernel/application/use-cases/document-upload/selectors/document-upload-selectors";
import { useApiAction } from "@shared-kernel/primary/shared/api-errors/use-api-action";
import { applyApiFieldErrors } from "@shared-kernel/primary/shared/api-errors/apply-api-field-errors";
import type { ActionForm } from "@shared-kernel/primary/shared/api-errors/action-form";
import { useResetWhenSettled } from "@shared-kernel/primary/shared/request-state/use-reset-when-settled";
import { POSTAL_ADDRESS_FIELD_RENAMES } from "@shared-kernel/primary/forms/validations/postal-address.schema";
import { useTeacherPedagogyUploads } from "@user-management-context/primary/teacher/teacher-pedagogy-subform/use-teacher-pedagogy-uploads";
import type { TeacherOnboardingFormInputs } from "./form-validation/full-form.schema";
import { formatFormDataToOnboardTeacherBody } from "./form-validation/shared";

export const useTeacherOnboarding = (form: ActionForm<TeacherOnboardingFormInputs>) => {
  const navigate = useNavigate();
  const run = useApiAction();
  const { processing } = useAppSelector(selectTeacherOnboarding);
  useResetWhenSettled(processing, resetOnboardTeacher);
  // L'upload est un singleton partagé : remis à zéro une fois son issue rendue
  const { processing: uploadProcessing } = useAppSelector(selectDocumentUpload);
  useResetWhenSettled(uploadProcessing, resetUploadDocument);
  const { uploadResume, uploadMethodologyDocument } = useTeacherPedagogyUploads(form);

  // L'API dit pourquoi le profil est refusé (adresse incomplète, fichier invalide), sous le champ fautif
  const onboard = useCallback(
    async (values: TeacherOnboardingFormInputs) => {
      const outcome = await run(onboardTeacher(formatFormDataToOnboardTeacherBody(values)), {
        success: "Votre profil enseignant est prêt !",
        failure: "Erreur lors de la finalisation de votre profil enseignant",
      });
      if (!outcome.ok) {
        applyApiFieldErrors(form, outcome.error, POSTAL_ADDRESS_FIELD_RENAMES);
        return;
      }
      navigate("/onboarding");
    },
    [run, navigate, form],
  );

  return { onboard, uploadResume, uploadMethodologyDocument };
};
